import { useAppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { Skeleton, Stack } from "@mui/material";
import {
	type GetDPSDetailResponse,
	type GetLifeDetailResponse,
	type GetPPDetailResponse,
	InsuredPersonRole,
	PensionPhaseStatus,
	ProductType,
} from "@repo/api";
import { readEnv } from "@repo/helpers";
import { AllowedChangesBooleanKey, useAllowedChanges, useAppSelector } from "@repo/logic";
import { BodyTextM, cs, HeadingL, MuiImage, useIsDesktop } from "@repo/ui";
import passportImage from "@repo/ui/assets/images/passport.png";
import { Button } from "gatsby-theme-material-ui";
import { type FC, useState } from "react";
import { useContractId } from "../../context/ContractIDContext";
import { useContractTypeContext } from "../../context/ContractTypeContext";
import { dateTimeFormat } from "../../functions/dates";
import { useCountry } from "../../hooks/useCountry";
import { useHasCurrentContactsChange } from "../../hooks/useHasCurrentContactsChange";
import { useContractById, useLifeDetail } from "../../queryHooks";
import AddressTemplate from "../AddressBox/AddressTemplate";
import Attention from "../AttentionBlock";
import ChangeAddressDrawer from "../Drawer/ChangeAddressDrawer";
import InfoIconDrawer from "../Drawer/InfoIconDrawer";
import Modal from "../Drawer/Modal";
import NotInMVP from "../Drawer/NotInMVP";
import InformationList from "../InformationList";
import { InternalButtonLink } from "../Link";
interface ClientSharedProps {
	person?: GetDPSDetailResponse | GetPPDetailResponse;
	isSuccess: boolean;
	isLoading: boolean;
}

const getIsChangeDisabled = ({
	isNotLifePolicyHolder,
	allowedChangesSuccess,
	canChange,
	canChangeOverride,
	isChangeActive,
}: {
	isNotLifePolicyHolder: boolean;
	allowedChangesSuccess: boolean;
	canChange: boolean | null | undefined;
	canChangeOverride: boolean | undefined;
	isChangeActive: boolean;
}) => {
	if (canChangeOverride !== undefined) {
		return canChangeOverride !== true;
	}

	if (isNotLifePolicyHolder) {
		return true;
	}

	if (!allowedChangesSuccess) {
		return true;
	}

	return canChange !== true || isChangeActive;
};

const renderElectronicContactsAction = ({
	isFeatureEnabled,
	contactChangeSuccess,
	isElectronicContactsDisabled,
	onNotInMvpClick,
}: {
	isFeatureEnabled: boolean;
	contactChangeSuccess: boolean;
	isElectronicContactsDisabled: boolean;
	onNotInMvpClick: () => void;
}) => {
	if (!isFeatureEnabled) {
		return <NotInMVP onClick={onNotInMvpClick} />;
	}

	if (!contactChangeSuccess) {
		return <Skeleton variant="rounded" width={64} height={16} />;
	}

	return (
		<InternalButtonLink
			variant="text"
			size="small"
			to="/jsou-vase-udaje-spravne"
			href="/jsou-vase-udaje-spravne"
			disabled={isElectronicContactsDisabled}
		>
			{cs.global.change}
		</InternalButtonLink>
	);
};

const ClientShared: FC<ClientSharedProps> = ({
	person,
	isSuccess,
	isLoading,
}) => {
	const isDesktop = useIsDesktop();
	const [isDrawerOpen, setIsDrawerOpen] = useState(false);
	const [formWasSend, setFormWasSend] = useState<boolean>(false);
	const { contractType } = useContractTypeContext();
	const { data: hasContactChange, isSuccess: contactChangeSuccess } =
		useHasCurrentContactsChange();

	const ai = useAppInsightsContext();

	const handleClick = (changeText: string) => {
		ai.trackEvent({
			name: `[Change] ${changeText}`,
			properties: {
				type: "not in MVP",
			},
		});
	};

	const {
		clientInfo: {
			firstName,
			lastName,
			birthNumber,
			dateOfBirth,
			degreeBefore,
			degreeAfter,
			citizenship,
		},
		citizenship: citizenshipLife,
		permanentAddress,
		contactAddress,
		contact,
		isContactAddressChangeInProgress,
	} = (isSuccess && person)
			? person
			: {
				clientInfo: {
					firstName: undefined,
					lastName: undefined,
					birthNumber: undefined,
					dateOfBirth: undefined,
					degreeBefore: undefined,
					degreeAfter: undefined,
					citizenship: undefined,
				},
				citizenship: undefined,
				permanentAddress: undefined,
				contactAddress: undefined,
				contact: {
					phone1: undefined,
					phone2: undefined,
					mobile: undefined,
					email: undefined,
				},
				isContactAddressChangeInProgress: undefined,
			};

	const citizenshipLong = useCountry(
		citizenship || citizenshipLife
	)?.country_name_cz_long;

	const contactInfoItems = [
		contact?.phone1 && {
			title: cs.global.phone,
			description: contact.phone1,
		},
		contact?.phone2 && {
			title: `${cs.global.phone} 2`,
			description: contact.phone2,
		},
		contact?.mobile && {
			title: cs.global.mobilePhone,
			description: contact.mobile,
		},
		contact?.email && {
			title: cs.global.email,
			description: contact.email,
		},
	].filter((item) => typeof item === "object" && item !== null);

	const { contractId: id } = useContractId();
	const { data } = useContractById({
		id: id,
		options: { refetchOnMount: false },
	});
	const { data: allowedChangesData, isSuccess: allowedChangesSuccess } = useAllowedChanges({
		id,
		options: { enabled: Boolean(id) },
	});
	const contactAddressOverride = useAppSelector((state) =>
		id
			? state.allowedChanges.overrides[id]?.[AllowedChangesBooleanKey.CanChangeContactAddress]
			: undefined,
	);
	const electronicContactsOverride = useAppSelector((state) =>
		id
			? state.allowedChanges.overrides[id]?.[AllowedChangesBooleanKey.CanChangeElectronicContacts]
			: undefined,
	);
	const contactAddressInProgressOverride = useAppSelector((state) =>
		id
			? state.allowedChanges.overrides[id]?.[AllowedChangesBooleanKey.IsContactAddressChangeInProgress]
			: undefined,
	);
	const electronicContactsInProgressOverride = useAppSelector((state) =>
		id
			? state.allowedChanges.overrides[id]?.[AllowedChangesBooleanKey.IsElectronicContactsChangeInProgress]
			: undefined,
	);

	const { data: life } = useLifeDetail<GetLifeDetailResponse>({
		id,
		contractType: ProductType.Cl,
		options: {
			enabled: contractType === ProductType.Cl,
		},
	});
	const policyHolder = life?.insuredPersons.find(
		(p) =>
			InsuredPersonRole.PolicyHolder === p.role ||
			InsuredPersonRole.PolicyHolderAndFirst === p.role ||
			InsuredPersonRole.PolicyHolderAndSecond === p.role
	);
	const isNotLifePolicyHolder =
		policyHolder?.birthNumber !== birthNumber &&
		contractType === ProductType.Cl;
	const effectiveContactAddressChangeActive =
		contactAddressInProgressOverride ??
		(formWasSend || Boolean(isContactAddressChangeInProgress));
	const effectiveElectronicContactsChangeActive =
		electronicContactsInProgressOverride ??
		Boolean(contactChangeSuccess && hasContactChange);
	const isContactAddressDisabled = getIsChangeDisabled({
		isNotLifePolicyHolder,
		allowedChangesSuccess,
		canChange: allowedChangesData?.canChangeContactAddress,
		canChangeOverride: contactAddressOverride,
		isChangeActive: effectiveContactAddressChangeActive,
	});
	const isElectronicContactsDisabled = getIsChangeDisabled({
		isNotLifePolicyHolder,
		allowedChangesSuccess,
		canChange: allowedChangesData?.canChangeElectronicContacts,
		canChangeOverride: electronicContactsOverride,
		isChangeActive: effectiveElectronicContactsChangeActive,
	});
	const isContactAddressChangeActive = effectiveContactAddressChangeActive;
	const isElectronicContactsChangeActive = effectiveElectronicContactsChangeActive;
	const isContactChangeFeatureEnabled =
		readEnv("GATSBY_FEATURE_IS_CONTACT_CHANGE_ENABLED") === "true";
	const electronicContactsAction = renderElectronicContactsAction({
		isFeatureEnabled: isContactChangeFeatureEnabled,
		contactChangeSuccess,
		isElectronicContactsDisabled,
		onNotInMvpClick: () => {
			handleClick("Kontaktní údaje");
		},
	});

	return (
		<Stack
			gap={{ md: 5, xs: 0 }}
			sx={{
				flexDirection: { md: "row" },
				alignItems: { md: "flex-end" },
			}}
		>
			<Stack
				spacing={5}
				sx={{
					width: { md: "50%" },
					mr: { md: 4 },
				}}
			>
				<HeadingL withoutScale>Osobní informace</HeadingL>
				{data?.pensionPhaseStatus === PensionPhaseStatus.Payout && (
					<Attention severity="warning">
						{cs.profile.person.changeContactInfoTerminatedContract}
					</Attention>
				)}
				<InformationList
					isLoading={isLoading}
					title="Osobní údaje"
					action={
						<NotInMVP
							onClick={() => {
								handleClick("Osobní údaje");
							}}
						/>
					}
					information={[
						{
							title: "Titul před jménem",
							description: degreeBefore ?? "-",
						},
						{
							title: "Jméno",
							description:
								contractType === ProductType.Cl
									? firstName ?? "-"
									: firstName,
						},
						{
							title: "Příjmení",
							description:
								contractType === ProductType.Cl ? lastName ?? "-" : lastName,
						},
						{
							title: "Titul za jménem",
							description: degreeAfter ?? "-",
						},
						{
							title: "Datum narození",
							description:
								dateOfBirth && dateTimeFormat.format(new Date(dateOfBirth)),
						},
						{
							title: <>Rodné číslo (číslo&nbsp;pojištěnce)</>,
							description: birthNumber ?? "-",
						},
						{
							title: "Státní občanství",
							description: citizenshipLong ?? "-",
						},
					]}
					sx={{ mb: 5 }}
				/>
				{permanentAddress && (
					<InformationList
						isLoading={isLoading}
						title={cs.global.permanentResidence}
						action={
							<Button
								variant="action"
								onClick={() => {
									setIsDrawerOpen(true);
								}}
							>
								<BodyTextM>{cs.global.change}</BodyTextM>
							</Button>
						}
						information={[
							{
								title: "Adresa",
								description: <AddressTemplate address={permanentAddress} />,
							},
						]}
					/>
				)}

				<Modal
					open={isDrawerOpen}
					onClose={() => {
						setIsDrawerOpen(false);
					}}
					onOpen={() => {
						setIsDrawerOpen(true);
					}}
				>
					{data?.type === ProductType.Uf || data?.type === ProductType.Pf ? (
						<InfoIconDrawer
							title={cs.global.permanentResidence}
							description={
								<span
									dangerouslySetInnerHTML={{
										__html: cs.profile.person.dpsPermanentAddressInfo,
									}}
								></span>
							}
							bottomButtonAction={() => {
								setIsDrawerOpen(false);
							}}
						/>
					) : (
						<InfoIconDrawer
							title={cs.global.permanentResidence}
							description={
								<span
									dangerouslySetInnerHTML={{
										__html: cs.profile.person.lifePermanentAddressInfo,
									}}
								></span>
							}
							bottomButtonAction={() => {
								setIsDrawerOpen(false);
							}}
						/>
					)}
				</Modal>
			</Stack>
			<Stack sx={{ width: { md: "50%" }, ml: { md: 4 } }}>
				{isDesktop && (
					<MuiImage
						sx={{
							height: 180,
							objectFit: "contain",
						}}
						src={passportImage}
					/>
				)}
				<Stack
					sx={{
						flexDirection: { md: "column-reverse" },
						gap: { md: 5, xs: 0 },
					}}
				>
					<Stack spacing={4}>
						<InformationList
							isLoading={isLoading}
							title="Kontaktní adresa"
							action={
								readEnv("GATSBY_FEATURE_IS_CONTACT_ADDRESS_CHANGE_ENABLED") === "true" ? (
									<ChangeAddressDrawer
										type={data?.type}
										contractNumber={data?.id}
										title={data?.title}
										disabled={isContactAddressDisabled}
										setFormWasSend={setFormWasSend}
									/>
								) : (
									<NotInMVP
										sx={{
											ml: { md: "40%", sm: "0" },
										}}
										text={cs.changeAddress.changeContactAddress}
										onClick={() =>
											handleClick(cs.profile.person.changeContactAddress)
										}
									/>
								)
							}
							information={[
								{
									title: "Adresa",
									description: <AddressTemplate address={contactAddress} />,
								},
							]}
						/>

						{isContactAddressChangeActive &&
							!isNotLifePolicyHolder && (
								<Attention severity="info" sx={{ mt: { xs: 5 } }}>
									{cs.global.contactAddressChangeInProgress}
								</Attention>
							)}
					</Stack>
					<Stack spacing={4}>
						{!data?.isChildrenContract && contactInfoItems.length !== 0 && (
							<InformationList
								isLoading={isLoading}
								title="Kontaktní údaje"
								action={electronicContactsAction}
								sx={{ mt: { md: 0, xs: 5 } }}
								information={contactInfoItems}
							/>
						)}
						{isElectronicContactsChangeActive &&
							!isNotLifePolicyHolder && (
								<Attention severity="info" sx={{ mt: { xs: 5 } }}>
									{cs.global.contactChangeAlreadyInProgress}
								</Attention>
							)}
					</Stack>
				</Stack>
			</Stack>
		</Stack>
	);
}
export default ClientShared;
